import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { ordersApi, refreshAccessToken, getLastRefreshFailure, isTransientRefreshFailure } from "../services/api";
import { useAuth } from "./AuthContext";
import { useStore } from "./StoreContext";
import { formatCurrency } from "../utils/currency";
import { useLeaderElection } from "../hooks/useLeaderElection";

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

// SSE hatasında refresh eşiği — AuthContext.tsx:297'deki proaktif refresh eşiğiyle (5 dk) aynı tutulmalı
export const SSE_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

// SSE hatası token'ın bittiğini göstermez (sayfa kapanışı da SSE'yi koparır). Token'da eşikten fazla süre varsa
// refresh atlanır: sayfa kapanırken başlayan refresh'in yanıtı kaybolunca sunucu eski refresh token'ı iptal etmiş
// oluyor ve oturum düşüyordu. Okunamayan/eksik/bozuk değerde bugünkü davranış ("try") korunur.
export function sseRefreshDecision(
  authUserRaw: string | null,
  nowMs: number
): { action: "skip" | "try"; remainingMs?: number } {
  if (!authUserRaw) return { action: "try" };
  try {
    const expiresAt = JSON.parse(authUserRaw)?.token_expires_at;
    if (typeof expiresAt !== "number" || !Number.isFinite(expiresAt)) return { action: "try" };
    const remainingMs = expiresAt - nowMs;
    return remainingMs > SSE_REFRESH_THRESHOLD_MS ? { action: "skip", remainingMs } : { action: "try", remainingMs };
  } catch {
    return { action: "try" };
  }
}

// [F2C] Akış adresi: yalnız tek kullanımlık bilet taşır — oturum token'ı adrese (ve nginx loguna) konmaz
export function sseStreamUrl(apiUrl: string, ticket: string): string {
  return `${apiUrl}/api/orders/stream?ticket=${encodeURIComponent(ticket)}`;
}

interface OrderProduct {
  lineItemId: string;
  legacyItemId: string;
  itemId?: string;
  title: string;
  sku: string;
  quantity: number;
  lineItemCost: string;
  image?: string;
  linked_asin?: string;
  amazon_domain?: string;
}

interface ShippingAddress {
  name?: string;
  fullName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateOrProvince?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  phoneNumber?: string;
}

export interface EbayOrder {
  id: string;
  orderId: string;
  legacyOrderId?: string;
  customer: string;
  buyerUsername: string;
  product: string;
  products: OrderProduct[];
  orderDate: string;
  amount: string;
  amountValue: number;
  currency: string;
  payment: string;
  paymentStatus: string;
  status: string;
  orderFulfillmentStatus: string;
  cancelStatus: string;
  shippingAddress: ShippingAddress;
  amazonOrderStatus?: string;
  amazonOrderId?: string;
  amazonOrderTotal?: number;
  aoError?: string;
  aoRetryCount?: number;
  aoSkip?: boolean;
  trackingInfo?: {
    trackingNumber?: string;
    aquilineTrackingNumber?: string;
    carrierProvider?: string;
    ebayUploadStatus?: string;
    status?: string;
    realTrackingNumber?: string;
    realCarrier?: string;
    carrier?: string;
    trackingUrl?: string;
    source?: string;
  } | null;
}

interface OrdersContextType {
  orders: EbayOrder[];
  loading: boolean;
  error: string | null;
  total: number;
  lastFetched: Date | null;
  refreshOrders: (force?: boolean) => Promise<void>;
  getOrderById: (orderId: string) => EbayOrder | undefined;
  // Status counts for tabs
  statusCounts: {
    all: number;
    pending: number;
    toShip: number;
    dispatched: number;
    delivered: number;
    cancelled: number;
  };
}

const OrdersContext = createContext<OrdersContextType>({
  orders: [],
  loading: true,
  error: null,
  total: 0,
  lastFetched: null,
  refreshOrders: async () => {},
  getOrderById: () => undefined,
  statusCounts: {
    all: 0,
    pending: 0,
    toShip: 0,
    dispatched: 0,
    delivered: 0,
    cancelled: 0,
  },
});

export const useOrders = () => useContext(OrdersContext);

interface OrdersProviderProps {
  children: ReactNode;
}

export const OrdersProvider: React.FC<OrdersProviderProps> = ({ children }) => {
  const { isAuthenticated, token } = useAuth();
  const { selectedStore, marketplace } = useStore();

  // Store-aware currency formatter (falls back to GB/£ when no store selected)
  const formatPrice = useCallback(
    (val: any) => formatCurrency(val, marketplace),
    [marketplace]
  );

  const [orders, setOrders] = useState<EbayOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [sseConnected, setSseConnected] = useState(false);

  // Leader Election: Aynı kullanıcının N sekmesinden yalnızca leader olan SSE açar.
  const { isLeader } = useLeaderElection();

  const fetchingRef = useRef(false);
  const lastStoreIdRef = useRef<number | undefined>(undefined);
  const eventSourceRef = useRef<EventSource | null>(null);
  const sseNesilRef = useRef(0); // [F2C] bilet beklenirken yeni bağlantı / çıkış başladıysa eski bilet akış açmaz
  const sseRetryCountRef = useRef(0);
  const sseRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sseDisabledRef = useRef(false); // Permanently disable SSE if too many errors
  const sseTokenRefreshAttemptsRef = useRef(0); // Track token refresh attempts for SSE
  const fetchOrdersRef = useRef<((force?: boolean) => Promise<void>) | null>(null);

  // Calculate status counts
  const statusCounts = React.useMemo(() => {
    const counts = {
      all: orders.length,
      pending: 0,
      toShip: 0,
      dispatched: 0,
      delivered: 0,
      cancelled: 0,
    };

    orders.forEach((order) => {
      const status = order.status?.toLowerCase() || "";
      if (status === "pending") counts.pending++;
      else if (status === "to ship") counts.toShip++;
      else if (status === "dispatched") counts.dispatched++;
      else if (status === "delivered") counts.delivered++;
      else if (status === "cancelled") counts.cancelled++;
    });

    return counts;
  }, [orders]);

  // Fetch orders from API
  const fetchOrders = useCallback(async (force = false) => {
    // Prevent concurrent fetches
    if (fetchingRef.current) return;

    // Check if store changed - force refresh if so
    const storeChanged = lastStoreIdRef.current !== selectedStore?.id;
    if (storeChanged) {
      lastStoreIdRef.current = selectedStore?.id;
      force = true; // Force refresh on store change
    }

    // Check if cache is still valid (unless force refresh)
    if (!force && lastFetched) {
      const cacheAge = Date.now() - lastFetched.getTime();
      if (cacheAge < CACHE_TTL) {
        return; // Cache is still valid
      }
    }

    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const params: any = { limit: 10000 };
      if (selectedStore?.id) {
        params.store_id = selectedStore.id;
      }
      const data = await ordersApi.getOrders(params);

      if (data.success && data.items) {
        const mappedOrders: EbayOrder[] = data.items.map((item: any) => {
          const status = item.status || "Pending";

          return {
            id: item.id?.toString() || "",
            orderId: item.orderId || "",
            legacyOrderId: item.legacyOrderId,
            customer: item.buyerName || item.buyerUsername || "Unknown",
            buyerUsername: item.buyerUsername || "",
            product: item.lineItems?.[0]?.title || "Order",
            products: item.lineItems || [],
            orderDate: item.orderDate || item.createdAt,
            amount: formatPrice(item.totalAmount || 0),
            amountValue: item.totalAmount || 0,
            currency: item.currency || "GBP",
            payment: "PayPal",
            paymentStatus: item.paymentStatus || "PENDING",
            status: status,
            orderFulfillmentStatus: item.fulfillmentStatus || "NOT_STARTED",
            cancelStatus: item.cancelStatus,
            shippingAddress: item.shippingAddress || {},
            amazonOrderStatus: item.amazonOrderStatus || "NONE",
            amazonOrderId: item.amazonOrderId || null,
            trackingInfo: item.trackingInfo || null,
          };
        });

        // Sort by date (newest first)
        const sortedOrders = mappedOrders.sort((a, b) => {
          const dateA = a.orderDate ? Date.parse(a.orderDate) : 0;
          const dateB = b.orderDate ? Date.parse(b.orderDate) : 0;
          return dateB - dateA;
        });

        setOrders(sortedOrders);
        setTotal(data.total || sortedOrders.length);
        setLastFetched(new Date());
      }
    } catch (err: any) {
      if (err.message !== "Unauthorized") {
        console.error("Failed to fetch orders:", err);
        setError(err.message || "Failed to load orders");
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [lastFetched, isAuthenticated, selectedStore?.id]);

  // Keep ref updated for SSE event handler
  fetchOrdersRef.current = fetchOrders;

  // Get order by ID
  const getOrderById = useCallback(
    (orderId: string) => {
      return orders.find((o) => o.orderId === orderId || o.id === orderId);
    },
    [orders]
  );

  // Refresh orders (public method)
  const refreshOrders = useCallback(
    async (force = false) => {
      await fetchOrders(force);
    },
    [fetchOrders]
  );

  // SSE connection for real-time updates with exponential backoff
  const connectSSE = useCallback(() => {
    // Leader Election guard: yalnız leader sekme SSE açar
    if (!isLeader) {
      return;
    }

    // Don't connect if permanently disabled
    if (sseDisabledRef.current) {
      console.log('[SSE] SSE disabled due to too many errors, using polling only');
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Clear any pending retry
    if (sseRetryTimeoutRef.current) {
      clearTimeout(sseRetryTimeoutRef.current);
      sseRetryTimeoutRef.current = null;
    }

    // Don't connect if no token
    if (!token) {
      return;
    }

    // [F2C] Oturum token'ı adrese konmaz: Authorization başlığıyla kısa ömürlü, tek kullanımlık akış bileti alınır (apiFetch
    // 401'de token'ı kendisi yeniler), akış bilet ile açılır. Her (yeniden) bağlanma yeni bilet alır.
    // Helper: Normal retry logic with exponential backoff
    const handleSseRetry = () => {
      const MAX_RETRIES = 10;
      if (sseRetryCountRef.current >= MAX_RETRIES) {
        console.error('[SSE] Max retries reached, disabling SSE permanently. Using polling only.');
        sseDisabledRef.current = true;
        return;
      }

      const retryDelay = Math.min(1000 * Math.pow(2, sseRetryCountRef.current), 60000);
      sseRetryCountRef.current++;

      console.log(`[SSE] Retrying in ${retryDelay / 1000}s... (attempt ${sseRetryCountRef.current}/${MAX_RETRIES})`);

      sseRetryTimeoutRef.current = setTimeout(() => {
        if (isAuthenticated && token && !sseDisabledRef.current) {
          connectSSE();
        }
      }, retryDelay);
    };

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
    const nesil = ++sseNesilRef.current;
    console.log(`[SSE] Requesting stream ticket (retry: ${sseRetryCountRef.current})`);
    ordersApi.getStreamTicket().then((res) => {
      if (nesil !== sseNesilRef.current) return; // bu arada yeni bağlantı başladı ya da çıkış yapıldı
      if (!res?.ticket) throw new Error("no ticket");
      openStream(res.ticket);
    }).catch((err) => {
      if (nesil !== sseNesilRef.current) return;
      console.warn('[SSE] Stream ticket failed, retrying with backoff:', err?.message || err);
      setSseConnected(false);
      handleSseRetry();
    });

    const openStream = (ticket: string) => {
    const es = new EventSource(sseStreamUrl(API_URL, ticket));
    eventSourceRef.current = es;

    es.addEventListener('connected', () => {
      setSseConnected(true);
      sseRetryCountRef.current = 0; // Reset retry count on successful connection
      console.log('[SSE] Connected successfully');
    });

    es.addEventListener('order_update', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE] Order update received:', data);

        // Refresh orders on any update
        // We use force=true to bypass cache and get fresh data
        fetchOrdersRef.current?.(true);

        // Leader: follower sekmelere bildir
        try {
          const bc = new BroadcastChannel("coderom-leader-v1");
          bc.postMessage({
            type: "ORDER_UPDATE",
            payload: { ts: Date.now() },
          });
          bc.close();
        } catch {
          // BroadcastChannel desteklenmiyorsa sessizce geç
        }

        // Dispatch window event for Order Detail page
        window.dispatchEvent(new CustomEvent('coderom-order-updated', {
          detail: data
        }));
      } catch (err) {
        console.error('[SSE] Failed to parse event data:', err);
      }
    });

    es.onerror = (error) => {
      console.error('[SSE] Connection error:', error);
      setSseConnected(false);
      es.close();
      eventSourceRef.current = null;

      // Token hâlâ geçerliyse refresh yapma, doğrudan yeniden bağlan (bkz. sseRefreshDecision)
      let authUserRaw: string | null = null;
      try {
        authUserRaw = localStorage.getItem("authUser");
      } catch {
        // localStorage erişilemezse karar "try" olur (bugünkü davranış)
      }
      const decision = sseRefreshDecision(authUserRaw, Date.now());
      if (decision.action === "skip") {
        console.debug(`[SSE] refresh skipped: token valid ~${Math.round((decision.remainingMs || 0) / 60000)} min`);
        handleSseRetry();
        return;
      }

      // Token refresh dene (max 2 kez) — EventSource 401 detayı vermez,
      // bu yüzden her error'da refresh deniyoruz
      if (sseTokenRefreshAttemptsRef.current < 2) {
        sseTokenRefreshAttemptsRef.current++;
        console.log(`[SSE] Attempting token refresh (attempt ${sseTokenRefreshAttemptsRef.current}/2)...`);
        refreshAccessToken().then((newToken) => {
          if (newToken) {
            console.log('[SSE] Token refreshed successfully, reconnecting SSE in 2s...');
            sseRetryCountRef.current = 0; // Retry sayacını sıfırla
            sseTokenRefreshAttemptsRef.current = 0; // Refresh sayacını sıfırla
            setTimeout(connectSSE, 2000); // Delay: ani reconnect döngüsünü önle
          } else if (isTransientRefreshFailure(getLastRefreshFailure())) {
            // 429 / 5xx / network: this attempt does not use up the refresh budget; reconnect with backoff
            console.warn(`[SSE] Token refresh transient failure (${getLastRefreshFailure()}), session kept, retrying later`);
            sseTokenRefreshAttemptsRef.current = Math.max(0, sseTokenRefreshAttemptsRef.current - 1);
            handleSseRetry();
          } else {
            console.warn('[SSE] Token refresh failed, falling back to retry logic');
            sseTokenRefreshAttemptsRef.current = 2; // Daha fazla refresh deneme
            // Normal retry logic'e düş
            handleSseRetry();
          }
        }).catch(() => {
          console.warn('[SSE] Token refresh error, falling back to retry logic');
          sseTokenRefreshAttemptsRef.current = 2;
          handleSseRetry();
        });
        return;
      }

      // Normal retry logic (token refresh başarısız veya tükendi)
      handleSseRetry();
    };
    }; // openStream
  }, [token, isAuthenticated, isLeader]);

  // SSE connection lifecycle
  useEffect(() => {
    if (isAuthenticated && token) {
      connectSSE();
    }

    // Cleanup on unmount or logout
    return () => {
      sseNesilRef.current++; // [F2C] yolda olan bilet isteği akış açmasın (çıkışta akış kapalı kalır)
      if (eventSourceRef.current) {
        console.log('[SSE] Closing connection');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        setSseConnected(false);
      }
      if (sseRetryTimeoutRef.current) {
        clearTimeout(sseRetryTimeoutRef.current);
        sseRetryTimeoutRef.current = null;
      }
      // Note: Don't reset retry count here - it causes reconnect loops
      // Let the retry logic handle it naturally on successful connection
    };
  }, [isAuthenticated, token, connectSSE, isLeader]);

  // Follower: leader sekmenin yayınladığı ORDER_UPDATE'i BC üzerinden dinle
  useEffect(() => {
    if (isLeader) {
      // Leader: BroadcastChannel dinlemeye gerek yok
      return;
    }

    // Follower: order_update mesajlarını BC'den al
    const bc = new BroadcastChannel("coderom-leader-v1");

    const handleBCMessage = (event: MessageEvent) => {
      if (
        event.data?.type === "ORDER_UPDATE" &&
        typeof event.data?.payload === "object"
      ) {
        // Kendi fetchOrders'ımızı tetikle
        if (fetchOrdersRef.current) {
          fetchOrdersRef.current(true);
        }
        // Order Detail sayfası için event
        window.dispatchEvent(
          new CustomEvent("coderom-order-updated", {
            detail: event.data.payload,
          })
        );
      }
    };

    bc.addEventListener("message", handleBCMessage);

    return () => {
      bc.removeEventListener("message", handleBCMessage);
      bc.close();
    };
  }, [isLeader]);

  // Fetch orders when user logs in (token changes)
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchOrders(true); // Force fetch on login
    } else {
      // User logged out - clear orders
      setOrders([]);
      setTotal(0);
      setLastFetched(null);
      setLoading(false);
    }
  }, [isAuthenticated, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch orders when store changes
  useEffect(() => {
    if (isAuthenticated && selectedStore?.id) {
      fetchOrders(true); // Force fetch on store change
    }
  }, [selectedStore?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh with adaptive polling (SSE primary, polling fallback)
  useEffect(() => {
    if (!isAuthenticated) return;

    // If SSE connected, use slower polling as backup (30 min)
    // If SSE disconnected, use normal polling (5 min)
    const pollInterval = sseConnected ? 30 * 60 * 1000 : CACHE_TTL;

    const interval = setInterval(() => {
      // Leader Election: yalnız leader sekme polling yapar
      if (!isLeader) return;
      fetchOrders(true); // Force refresh
    }, pollInterval);

    return () => clearInterval(interval);
  }, [isAuthenticated, sseConnected, fetchOrders, isLeader]);

  // Visibility API: Close SSE when tab hidden, reconnect when visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        // EC-4 fallback: BroadcastChannel desteklenmiyorsa
        // useLeaderElection abdicate etmez, bu blok tek koruma.
        // Normal akışta useLeaderElection heartbeat timeout ile
        // yönetir — bu blok no-op olur.
        if (typeof BroadcastChannel === 'undefined') {
          sseNesilRef.current++; // [F2C] yolda olan bilet isteği gizli sekmede akış açmasın
          if (eventSourceRef.current) {
            console.log('[SSE] Tab hidden (BC fallback), closing');
            eventSourceRef.current.close();
            eventSourceRef.current = null;
            setSseConnected(false);
          }
          if (sseRetryTimeoutRef.current) {
            clearTimeout(sseRetryTimeoutRef.current);
            sseRetryTimeoutRef.current = null;
          }
        }
      }
      // visible bloğu kaldırıldı:
      // useLeaderElection onVisibility kendi election/no-op
      // kararını verir; isLeader değişimi zaten SSE useEffect'i
      // tetikler (dep array'de isLeader var).
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  return (
    <OrdersContext.Provider
      value={{
        orders,
        loading,
        error,
        total,
        lastFetched,
        refreshOrders,
        getOrderById,
        statusCounts,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
};

export default OrdersContext;
